import { useCallback, useState } from 'react';
import { useRequest } from 'ahooks';
import { Popover, Spin } from 'antd';
import { queryItemListByInternalItemIds } from '@knowledge/common/components/ItemSelectV2/api';
import { ItemSelectV2 } from '@knowledge/common/components/ItemSelectV2';
import moreSvg from '@knowledge/assets/more.svg';
import { useTranslate } from '@tolgee/react';
import { Spu, SpuList, SpinContainer } from '@knowledge/common/components/SpuBasic';
import { EditOutlined } from '@ant-design/icons';

type ItemSelectProps = {
  storeType: Knowledge.StoreType;
  storeScopes: General.StoreScope[];
  emptyRender?: (show: () => void) => void;
} & Pick<Parameters<typeof ItemSelectV2>[0], 'value' | 'onChange'>;

/**
 * 商品选择器字段和接口字段映射
 */
function transformBtoA(value: ItemSelectProps['value']) {
  return value?.map((each) => {
    const {
      internalItemId: itemInternalId,
      platform,
      region,
      itemName: title,
      itemUrl,
      externalShopId: shopId,
      externalItemId: spuId,
      skuInfo,
    } = each;

    return {
      itemInternalId,
      platform,
      region,
      shopId,
      title,
      itemUrl,
      spuId,
      skus: skuInfo?.map((e) => {
        return {
          skuId: e.skuId,
          sellerSku: e.sellerSku,
          skuName: e.skuName,
        };
      }),
    };
  });
}

function transformAtoB(value: Knowledge.SpecifiedItem[] = []) {
  return value?.map((each) => {
    const { itemInternalId, spuId, skus } = each;
    return {
      internalItemId: itemInternalId,
      externalItemId: spuId,
      skuInfo: skus,
    };
  });
}

function ItemSelect(props: ItemSelectProps) {
  const { value, onChange, storeType, storeScopes, emptyRender } = props;
  const { t } = useTranslate();
  const [visible, setVisible] = useState(false);
  const onValueChange = useCallback(
    (value) => {
      onChange(transformBtoA(value));
    },
    [onChange],
  );

  return (
    <div>
      {!value?.length ? (
        emptyRender ? (
          emptyRender(() => setVisible(true))
        ) : (
          <div
            className="text-[14px] leading-[20px] cursor-pointer text-[var(--fett-scenario-accent-text)] mt-[0px]"
            onClick={() => setVisible(true)}
          >
            + {t('aide.选择商品')}
          </div>
        )
      ) : (
        <>
          <Preview ids={value?.map((e) => e.itemInternalId)} onEdit={() => setVisible(true)} />
        </>
      )}

      <ItemSelectV2
        storeScopes={storeScopes}
        storeType={storeType}
        visible={visible}
        onCancel={() => setVisible(false)}
        value={transformAtoB(value)}
        onChange={onValueChange}
      />
    </div>
  );
}

export function Preview(props: { ids: string[]; onEdit?: () => void }) {
  const { ids, onEdit } = props;
  const { t } = useTranslate();
  const { data, loading } = useRequest(() => queryItemListByInternalItemIds(ids || []), {
    refreshDeps: [ids.join(',')],
  });
  const list = data?.itemList || [];
  return (
    <Popover
      content={
        <SpuList>
          {loading ? (
            <SpinContainer>
              <Spin />
            </SpinContainer>
          ) : (
            list.map((spu: any) => {
              spu.skus = spu.skuInfo;
              spu.title = spu.itemName;
              return (
                <li key={spu.spuId}>
                  <Spu spu={spu} />
                </li>
              );
            })
          )}
        </SpuList>
      }
    >
      <div className="inline-flex items-center">
        {t('knowledge.x 个商品', { x: ids?.length })}
        <img width={16} src={moreSvg} alt="more" className="ml-[4px]" />
        {onEdit && ids?.length > 0 && <EditOutlined className="ml-[6px]" onClick={onEdit} />}
      </div>
    </Popover>
  );
}

export default ItemSelect;
